import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';

export const ASSET_NAME = 'discount-codes';

// ================ Async thunks ================ //

export const fetchDiscountsThunk = createAsyncThunk(
  'DiscountsPage/fetchDiscounts',
  (params, { extra: sdk, rejectWithValue }) => {
    return sdk.custom
      .get({
        endpoint: '/api/discounts',
        params: { token: sdk.getToken() },
      })
      .then(response => {
        return response.data;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the fetchDiscounts thunk
export const fetchDiscounts = params => dispatch => {
  return dispatch(fetchDiscountsThunk(params)).unwrap();
};

export const createDiscountThunk = createAsyncThunk(
  'DiscountsPage/createDiscount',
  (discountData, { extra: sdk, rejectWithValue }) => {
    return sdk.custom
      .post({
        endpoint: '/api/discounts',
        body: {
          token: sdk.getToken(),
          discountData,
        },
      })
      .then(response => {
        return response.data;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the createDiscount thunk
export const createDiscount = discountData => dispatch => {
  return dispatch(createDiscountThunk(discountData)).unwrap();
};

export const updateDiscountThunk = createAsyncThunk(
  'DiscountsPage/updateDiscount',
  ({ discountId, discountData }, { extra: sdk, rejectWithValue }) => {
    return sdk.custom
      .put({
        endpoint: `/api/discounts/${discountId}`,
        body: {
          token: sdk.getToken(),
          discountData,
        },
      })
      .then(response => {
        return response.data;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the updateDiscount thunk
export const updateDiscount = (discountId, discountData) => dispatch => {
  return dispatch(updateDiscountThunk({ discountId, discountData })).unwrap();
};

export const deleteDiscountThunk = createAsyncThunk(
  'DiscountsPage/deleteDiscount',
  (discountId, { extra: sdk, rejectWithValue }) => {
    return sdk.custom
      .delete({
        endpoint: `/api/discounts/${discountId}`,
        params: { token: sdk.getToken() },
      })
      .then(response => {
        return response.data;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the deleteDiscount thunk
export const deleteDiscount = discountId => dispatch => {
  return dispatch(deleteDiscountThunk(discountId)).unwrap();
};

// ================ Slice ================ //

const discountsSlice = createSlice({
  name: 'DiscountsPage',
  initialState: {
    discounts: [],
    fetchDiscountsInProgress: false,
    fetchDiscountsError: null,
    createDiscountInProgress: false,
    createDiscountError: null,
    updateDiscountInProgress: false,
    updateDiscountError: null,
    deleteDiscountInProgress: false,
    deleteDiscountError: null,
  },
  reducers: {
    clearDiscountsErrors: state => {
      state.fetchDiscountsError = null;
      state.createDiscountError = null;
      state.updateDiscountError = null;
      state.deleteDiscountError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch discounts
      .addCase(fetchDiscountsThunk.pending, state => {
        state.fetchDiscountsInProgress = true;
        state.fetchDiscountsError = null;
      })
      .addCase(fetchDiscountsThunk.fulfilled, (state, action) => {
        state.fetchDiscountsInProgress = false;
        state.discounts = action.payload.discounts || [];
      })
      .addCase(fetchDiscountsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.fetchDiscountsInProgress = false;
        state.fetchDiscountsError = action.payload;
      })
      // Create discount
      .addCase(createDiscountThunk.pending, state => {
        state.createDiscountInProgress = true;
        state.createDiscountError = null;
      })
      .addCase(createDiscountThunk.fulfilled, (state, action) => {
        state.createDiscountInProgress = false;
        state.discounts.push(action.payload.discount);
      })
      .addCase(createDiscountThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.createDiscountInProgress = false;
        state.createDiscountError = action.payload;
      })
      // Update discount
      .addCase(updateDiscountThunk.pending, state => {
        state.updateDiscountInProgress = true;
        state.updateDiscountError = null;
      })
      .addCase(updateDiscountThunk.fulfilled, (state, action) => {
        state.updateDiscountInProgress = false;
        const updatedDiscount = action.payload.discount;
        const index = state.discounts.findIndex(d => d.id === updatedDiscount.id);
        if (index !== -1) {
          state.discounts[index] = updatedDiscount;
        }
      })
      .addCase(updateDiscountThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.updateDiscountInProgress = false;
        state.updateDiscountError = action.payload;
      })
      // Delete discount
      .addCase(deleteDiscountThunk.pending, state => {
        state.deleteDiscountInProgress = true;
        state.deleteDiscountError = null;
      })
      .addCase(deleteDiscountThunk.fulfilled, (state, action) => {
        state.deleteDiscountInProgress = false;
        const deletedDiscountId = action.payload.deletedDiscountId;
        state.discounts = state.discounts.filter(d => d.id !== deletedDiscountId);
      })
      .addCase(deleteDiscountThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.deleteDiscountInProgress = false;
        state.deleteDiscountError = action.payload;
      });
  },
});

export const { clearDiscountsErrors } = discountsSlice.actions;
export default discountsSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search) => dispatch => {
  const pageAsset = { discountCodesPage: `content/pages/${ASSET_NAME}.json` };
  return dispatch(fetchPageAssets(pageAsset, true));
};
